/**
 * UI controls for simulation configuration
 */

import type { SimConfig, TopType } from '../sim/types';
import { PRESETS, getPresetsForType, validateConfig } from './presets';

export type ControlsCallback = {
  onConfigChange: (config: SimConfig) => void;
  onStart: () => void;
  onPause: () => void;
  onReset: () => void;
  onExport: (format: 'csv' | 'json') => void;
};

export class SimulationControls {
  private container: HTMLElement;
  private currentConfig: SimConfig;
  private callbacks: ControlsCallback;

  constructor(containerId: string, callbacks: ControlsCallback) {
    const element = document.getElementById(containerId);
    if (!element) {
      throw new Error(`Container element ${containerId} not found`);
    }

    this.container = element;
    this.callbacks = callbacks;

    // Default configuration
    this.currentConfig = PRESETS.euler_tennis_racket.config;

    this.buildUI();

    // Initialize simulation with default configuration
    this.callbacks.onConfigChange(this.currentConfig);
  }

  private buildUI(): void {
    this.container.innerHTML = '';

    // Top type selector
    this.addSection('Top Type');
    this.addTopTypeSelector();

    // Preset selector
    this.addSection('Presets');
    this.addPresetSelector();

    // Configuration inputs
    this.addSection('Configuration', true);
    this.addConfigInputs();

    // Data export
    this.addSection('Data', false, 'Export simulation trajectory data as CSV file with state history');
    this.addDataExport();

    // Visualization options
    this.addSection('Visualization');
    this.addVisualizationOptions();
  }

  private addSection(title: string, addIcons: boolean = false, infoTooltip?: string): void {
    const section = document.createElement('div');
    section.className = 'control-group';

    const headingRow = document.createElement('div');
    headingRow.style.display = 'flex';
    headingRow.style.alignItems = 'center';
    headingRow.style.justifyContent = 'space-between';

    const headingContainer = document.createElement('div');
    headingContainer.style.display = 'flex';
    headingContainer.style.alignItems = 'center';
    headingContainer.style.gap = '0.5rem';

    const heading = document.createElement('h3');
    heading.textContent = title;
    heading.style.margin = '0';
    headingContainer.appendChild(heading);

    // Add info icon if tooltip provided
    if (infoTooltip) {
      const infoIcon = document.createElement('span');
      infoIcon.className = 'info-tooltip';
      infoIcon.textContent = 'i';
      infoIcon.style.cssText = 'font-size: 0.7rem; cursor: help;';

      // Add hover listeners for floating tooltip
      infoIcon.addEventListener('mouseenter', () => {
        const rect = infoIcon.getBoundingClientRect();
        let tooltipEl = document.getElementById('floating-tooltip');

        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'floating-tooltip';
          tooltipEl.className = 'floating-tooltip';
          document.body.appendChild(tooltipEl);
        }

        tooltipEl.textContent = infoTooltip;
        tooltipEl.style.left = (rect.right + 10) + 'px';
        tooltipEl.style.top = (rect.top + rect.height / 2) + 'px';
        tooltipEl.style.transform = 'translateY(-50%)';
        tooltipEl.classList.add('show');
      });

      infoIcon.addEventListener('mouseleave', () => {
        const tooltipEl = document.getElementById('floating-tooltip');
        if (tooltipEl) {
          tooltipEl.classList.remove('show');
        }
      });

      headingContainer.appendChild(infoIcon);
    }

    headingRow.appendChild(headingContainer);

    if (addIcons) {
      const iconGroup = document.createElement('div');
      iconGroup.style.display = 'flex';
      iconGroup.style.gap = '0.3rem';

      // Upload icon (SVG)
      const uploadBtn = document.createElement('button');
      uploadBtn.className = 'icon-btn config-icon-btn';
      uploadBtn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="17 8 12 3 7 8"/>
          <line x1="12" y1="3" x2="12" y2="15"/>
        </svg>
      `;
      uploadBtn.setAttribute('data-tooltip', 'Upload config');
      uploadBtn.style.cssText = 'width: 1.8rem; height: 1.8rem; padding: 0.25rem; margin: 0; position: relative; display: flex; align-items: center; justify-content: center;';

      // Add hover listeners for floating tooltip
      uploadBtn.addEventListener('mouseenter', () => {
        const rect = uploadBtn.getBoundingClientRect();
        let tooltipEl = document.getElementById('floating-tooltip');

        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'floating-tooltip';
          tooltipEl.className = 'floating-tooltip';
          document.body.appendChild(tooltipEl);
        }

        tooltipEl.textContent = 'Import simulation configuration from JSON file';
        tooltipEl.style.left = (rect.left + rect.width / 2) + 'px';
        tooltipEl.style.top = (rect.top - 8) + 'px';
        tooltipEl.style.transform = 'translate(-50%, -100%)';
        tooltipEl.classList.add('show');
      });

      uploadBtn.addEventListener('mouseleave', () => {
        const tooltipEl = document.getElementById('floating-tooltip');
        if (tooltipEl) {
          tooltipEl.classList.remove('show');
        }
      });

      iconGroup.appendChild(uploadBtn);

      // Download icon (SVG)
      const downloadBtn = document.createElement('button');
      downloadBtn.className = 'icon-btn config-icon-btn';
      downloadBtn.innerHTML = `
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
          <polyline points="7 10 12 15 17 10"/>
          <line x1="12" y1="15" x2="12" y2="3"/>
        </svg>
      `;
      downloadBtn.setAttribute('data-tooltip', 'Download config');
      downloadBtn.style.cssText = 'width: 1.8rem; height: 1.8rem; padding: 0.25rem; margin: 0; position: relative; display: flex; align-items: center; justify-content: center;';

      // Add hover listeners for floating tooltip
      downloadBtn.addEventListener('mouseenter', () => {
        const rect = downloadBtn.getBoundingClientRect();
        let tooltipEl = document.getElementById('floating-tooltip');

        if (!tooltipEl) {
          tooltipEl = document.createElement('div');
          tooltipEl.id = 'floating-tooltip';
          tooltipEl.className = 'floating-tooltip';
          document.body.appendChild(tooltipEl);
        }

        tooltipEl.textContent = 'Export simulation configuration as JSON file';
        tooltipEl.style.left = (rect.left + rect.width / 2) + 'px';
        tooltipEl.style.top = (rect.top - 8) + 'px';
        tooltipEl.style.transform = 'translate(-50%, -100%)';
        tooltipEl.classList.add('show');
      });

      downloadBtn.addEventListener('mouseleave', () => {
        const tooltipEl = document.getElementById('floating-tooltip');
        if (tooltipEl) {
          tooltipEl.classList.remove('show');
        }
      });

      iconGroup.appendChild(downloadBtn);

      headingRow.appendChild(iconGroup);

      // Hidden file input for import
      const fileInput = document.createElement('input');
      fileInput.type = 'file';
      fileInput.accept = '.json';
      fileInput.style.display = 'none';
      headingRow.appendChild(fileInput);

      // Set up event handlers
      uploadBtn.addEventListener('click', () => fileInput.click());

      fileInput.addEventListener('change', (e) => {
        const file = (e.target as HTMLInputElement).files?.[0];
        if (file) {
          const reader = new FileReader();
          reader.onload = (event) => {
            try {
              const config = JSON.parse(event.target?.result as string);
              const errors = validateConfig(config);
              if (errors) {
                const errorMsg = Array.isArray(errors) ? errors.join('\n') : errors;
                alert(`Invalid configuration:\n${errorMsg}`);
                return;
              }
              this.currentConfig = config;
              this.buildUI();
              this.callbacks.onConfigChange(this.currentConfig);
              this.callbacks.onReset();
              (window as any).showNotification?.('Configuration imported');
            } catch (err) {
              alert('Error reading configuration file');
            }
          };
          reader.readAsText(file);
        }
        fileInput.value = '';
      });

      downloadBtn.addEventListener('click', () => {
        const json = JSON.stringify(this.currentConfig, null, 2);
        const blob = new Blob([json], { type: 'application/json' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${this.currentConfig.type}_config.json`;
        a.click();
        URL.revokeObjectURL(url);
        (window as any).showNotification?.('Configuration exported');
      });
    }

    section.appendChild(headingRow);
    this.container.appendChild(section);
  }

  private addTopTypeSelector(): void {
    const lastGroup = this.container.lastElementChild as HTMLElement;

    const select = this.createSelect('top-type', [
      { value: 'euler', label: 'Euler Top (Free Rigid Body)' },
      { value: 'lagrange', label: 'Lagrange Top (With Gravity)' },
      { value: 'kovalevskaya', label: 'Kovalevskaya Top (Integrable)' }
    ], this.currentConfig.type);

    select.addEventListener('change', () => {
      const type = select.value as TopType;
      const presets = getPresetsForType(type);
      const firstPreset = Object.values(presets)[0];
      if (firstPreset) {
        // Pause simulation and update button state
        this.callbacks.onPause();
        const startPauseBtn = document.getElementById('start-pause-btn');
        if (startPauseBtn) {
          startPauseBtn.textContent = '▶';
        }

        this.currentConfig = { ...firstPreset.config };
        this.buildUI(); // Rebuild UI for new type
        this.callbacks.onConfigChange(this.currentConfig);
        this.callbacks.onReset(); // Reset to initial state
        (window as any).showNotification?.(`Switched to ${select.options[select.selectedIndex].text}`);
      }
    });

    lastGroup.appendChild(select);
  }

  private addPresetSelector(): void {
    const lastGroup = this.container.lastElementChild as HTMLElement;

    const presets = getPresetsForType(this.currentConfig.type);
    const options = Object.entries(presets).map(([key, preset]) => ({
      value: key,
      label: preset.name
    }));

    const select = this.createSelect('preset', options, Object.keys(presets)[0]);

    select.addEventListener('change', () => {
      const preset = presets[select.value];
      if (preset) {
        // Pause simulation and update button state
        this.callbacks.onPause();
        const startPauseBtn = document.getElementById('start-pause-btn');
        if (startPauseBtn) {
          startPauseBtn.textContent = '▶';
        }

        this.currentConfig = { ...preset.config };
        this.buildUI(); // Rebuild UI with new preset
        this.callbacks.onConfigChange(this.currentConfig);
        this.callbacks.onReset(); // Reset to initial state
        (window as any).showNotification?.(`Preset: ${preset.name}`);
      }
    });

    lastGroup.appendChild(select);

    // Description
    const presetKey = select.value;
    const description = document.createElement('p');
    description.className = 'preset-description';
    description.textContent = presets[presetKey]?.description || '';
    lastGroup.appendChild(description);
  }

  private createLabelWithTooltip(text: string, tooltip: string): HTMLElement {
    const label = document.createElement('h4');

    const textNode = document.createTextNode(text);
    label.appendChild(textNode);

    const tooltipIcon = document.createElement('span');
    tooltipIcon.className = 'info-tooltip';
    tooltipIcon.textContent = 'i';
    tooltipIcon.setAttribute('data-tooltip', tooltip);

    // Add hover listeners to show tooltip in fixed position
    tooltipIcon.addEventListener('mouseenter', () => {
      const rect = tooltipIcon.getBoundingClientRect();
      let tooltipEl = document.getElementById('floating-tooltip');

      if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = 'floating-tooltip';
        tooltipEl.className = 'floating-tooltip';
        document.body.appendChild(tooltipEl);
      }

      tooltipEl.textContent = tooltip;
      tooltipEl.style.left = (rect.right + 10) + 'px';
      tooltipEl.style.top = (rect.top + rect.height / 2) + 'px';
      tooltipEl.style.transform = 'translateY(-50%)';
      tooltipEl.classList.add('show');
    });

    tooltipIcon.addEventListener('mouseleave', () => {
      const tooltipEl = document.getElementById('floating-tooltip');
      if (tooltipEl) {
        tooltipEl.classList.remove('show');
      }
    });

    label.appendChild(tooltipIcon);
    return label;
  }

  private addConfigInputs(): void {
    const lastGroup = this.container.lastElementChild as HTMLElement;

    // Inertia - row layout
    const inertiaLabel = this.createLabelWithTooltip(
      'Inertia (kg⋅m²)',
      'Moment of inertia tensor diagonal elements'
    );
    lastGroup.appendChild(inertiaLabel);

    // For Lagrange: I1 = I2, for Kovalevskaya: I1 = I2 = 2*I3
    const enforceInertiaConstraints = (changedAxis: 'I1' | 'I2' | 'I3', value: number) => {
      if (this.currentConfig.type === 'lagrange') {
        // Lagrange: I1 = I2
        this.currentConfig.inertia.I1 = value;
        this.currentConfig.inertia.I2 = value;
      } else if (this.currentConfig.type === 'kovalevskaya') {
        // Kovalevskaya: I1 = I2 = 2*I3
        if (changedAxis === 'I1' || changedAxis === 'I2') {
          this.currentConfig.inertia.I1 = value;
          this.currentConfig.inertia.I2 = value;
          this.currentConfig.inertia.I3 = value / 2;
        } else {
          // Changed I3, so update I1 and I2
          this.currentConfig.inertia.I3 = value;
          this.currentConfig.inertia.I1 = value * 2;
          this.currentConfig.inertia.I2 = value * 2;
        }
      }
    };

    if (this.currentConfig.type === 'lagrange') {
      // For Lagrange, only show I1 and I3 (I2 = I1 automatically)
      this.addNumberInputRow(lastGroup,
        [
          { label: 'I₁=I₂', value: this.currentConfig.inertia.I1, min: 0.1, max: 10, step: 0.1, onChange: (v) => { enforceInertiaConstraints('I1', v); this.applyConfigAndPause(); } },
          { label: 'I₃', value: this.currentConfig.inertia.I3, min: 0.1, max: 10, step: 0.1, onChange: (v) => { this.currentConfig.inertia.I3 = v; this.applyConfigAndPause(); } }
        ]
      );
    } else if (this.currentConfig.type === 'kovalevskaya') {
      // For Kovalevskaya, only show I1 (I2 = I1, I3 = I1/2 automatically)
      this.addNumberInputRow(lastGroup,
        [
          { label: 'I₁=I₂', value: this.currentConfig.inertia.I1, min: 0.2, max: 10, step: 0.2, onChange: (v) => { enforceInertiaConstraints('I1', v); this.applyConfigAndPause(); } }
        ]
      );

      // Show computed I3 as read-only info
      const i3Info = document.createElement('div');
      i3Info.style.fontSize = '0.75rem';
      i3Info.style.color = 'var(--text-dim)';
      i3Info.style.marginTop = '0.3rem';
      i3Info.style.fontStyle = 'italic';
      i3Info.textContent = `I₃ = ${(this.currentConfig.inertia.I3).toFixed(2)} (= I₁/2)`;
      lastGroup.appendChild(i3Info);
    } else {
      // Euler top: all three independent
      this.addNumberInputRow(lastGroup,
        [
          { label: 'I₁', value: this.currentConfig.inertia.I1, min: 0.1, max: 10, step: 0.1, onChange: (v) => { this.currentConfig.inertia.I1 = v; this.applyConfigAndPause(); } },
          { label: 'I₂', value: this.currentConfig.inertia.I2, min: 0.1, max: 10, step: 0.1, onChange: (v) => { this.currentConfig.inertia.I2 = v; this.applyConfigAndPause(); } },
          { label: 'I₃', value: this.currentConfig.inertia.I3, min: 0.1, max: 10, step: 0.1, onChange: (v) => { this.currentConfig.inertia.I3 = v; this.applyConfigAndPause(); } }
        ]
      );
    }

    // Initial angular velocity - row layout
    const omegaLabel = this.createLabelWithTooltip(
      'Initial ω (rad/s)',
      'Initial angular velocity in body frame'
    );
    lastGroup.appendChild(omegaLabel);

    this.addNumberInputRow(lastGroup,
      [
        { label: 'ωₓ', value: this.currentConfig.omega0[0], min: -20, max: 20, step: 0.1, onChange: (v) => { this.currentConfig.omega0[0] = v; this.applyConfigAndPause(); } },
        { label: 'ωᵧ', value: this.currentConfig.omega0[1], min: -20, max: 20, step: 0.1, onChange: (v) => { this.currentConfig.omega0[1] = v; this.applyConfigAndPause(); } },
        { label: 'ω_z', value: this.currentConfig.omega0[2], min: -20, max: 20, step: 0.1, onChange: (v) => { this.currentConfig.omega0[2] = v; this.applyConfigAndPause(); } }
      ]
    );

    // Lagrange/Kovalevskaya specific parameters
    if (this.currentConfig.type !== 'euler') {
      const gravityLabel = this.createLabelWithTooltip(
        'Gravity Parameters',
        'Parameters for gravitational field interaction'
      );
      lastGroup.appendChild(gravityLabel);

      // COM - row layout
      const comLabel = document.createElement('label');
      comLabel.textContent = 'Center of Mass (m)';
      comLabel.style.fontSize = '0.85rem';
      comLabel.style.marginTop = '0.5rem';
      lastGroup.appendChild(comLabel);

      this.addNumberInputRow(lastGroup,
        [
          { label: 'x', value: this.currentConfig.com![0], min: -2, max: 2, step: 0.1, onChange: (v) => { if (this.currentConfig.type !== 'euler') { this.currentConfig.com![0] = v; this.applyConfigAndPause(); } } },
          { label: 'y', value: this.currentConfig.com![1], min: -2, max: 2, step: 0.1, onChange: (v) => { if (this.currentConfig.type !== 'euler') { this.currentConfig.com![1] = v; this.applyConfigAndPause(); } } },
          { label: 'z', value: this.currentConfig.com![2], min: -2, max: 2, step: 0.1, onChange: (v) => { if (this.currentConfig.type !== 'euler') { this.currentConfig.com![2] = v; this.applyConfigAndPause(); } } }
        ]
      );

      // Mass and gravity - row layout
      this.addNumberInputRow(lastGroup,
        [
          { label: 'Mass (kg)', value: this.currentConfig.mass!, min: 0.1, max: 10, step: 0.1, onChange: (v) => { if (this.currentConfig.type !== 'euler') { this.currentConfig.mass = v; this.applyConfigAndPause(); } } },
          { label: 'g (m/s²)', value: this.currentConfig.gravity!, min: 0, max: 20, step: 0.1, onChange: (v) => { if (this.currentConfig.type !== 'euler') { this.currentConfig.gravity = v; this.applyConfigAndPause(); } } }
        ]
      );
    }

    // Integration parameters
    const integrationLabel = this.createLabelWithTooltip(
      'Numerical Integration',
      'Numerical integration method and timestep'
    );
    lastGroup.appendChild(integrationLabel);

    // Timestep and time scale - row layout
    this.addNumberInputRow(lastGroup,
      [
        { label: 'dt (s)', value: this.currentConfig.dt, min: 0.0001, max: 0.01, step: 0.0001, onChange: (v) => { this.currentConfig.dt = v; this.applyConfigAndPause(); } },
        { label: 'Speedup', value: this.currentConfig.timeScale, min: 0.1, max: 4, step: 0.1, onChange: (v) => { this.currentConfig.timeScale = v; this.applyConfigAndPause(); } }
      ]
    );

    // Method selector
    const methodLabel = document.createElement('label');
    methodLabel.textContent = 'Integration Method';
    methodLabel.style.fontSize = '0.85rem';
    methodLabel.style.marginTop = '0.5rem';
    lastGroup.appendChild(methodLabel);

    const methodSelect = this.createSelect('method', [
      { value: 'rk4', label: 'RK4' },
      { value: 'midpoint', label: 'Midpoint' }
    ], this.currentConfig.method);

    methodSelect.addEventListener('change', () => {
      this.currentConfig.method = methodSelect.value as 'rk4' | 'midpoint';
      this.applyConfigAndPause();
    });

    lastGroup.appendChild(methodSelect);
  }

  private addDataExport(): void {
    const lastGroup = this.container.lastElementChild as HTMLElement;

    // Export CSV button only
    const exportCsvBtn = document.createElement('button');
    exportCsvBtn.textContent = 'Save Sim Trajectory';
    exportCsvBtn.className = 'btn-secondary';
    exportCsvBtn.addEventListener('click', () => this.callbacks.onExport('csv'));
    lastGroup.appendChild(exportCsvBtn);
  }

  private addVisualizationOptions(): void {
    const lastGroup = this.container.lastElementChild as HTMLElement;

    this.addCheckbox(lastGroup, 'Show Inertia Ellipsoid', true, (checked) => {
      window.dispatchEvent(new CustomEvent('viz-toggle', { detail: { type: 'ellipsoid', value: checked } }));
    });

    this.addCheckbox(lastGroup, 'Show Body Axes', true, (checked) => {
      window.dispatchEvent(new CustomEvent('viz-toggle', { detail: { type: 'bodyAxes', value: checked } }));
    });

    this.addCheckbox(lastGroup, 'Show World Axes', true, (checked) => {
      window.dispatchEvent(new CustomEvent('viz-toggle', { detail: { type: 'worldAxes', value: checked } }));
    });

    this.addCheckbox(lastGroup, 'Show Angular Momentum', true, (checked) => {
      window.dispatchEvent(new CustomEvent('viz-toggle', { detail: { type: 'angularMomentum', value: checked } }));
    });

    this.addCheckbox(lastGroup, 'Show Trail', true, (checked) => {
      window.dispatchEvent(new CustomEvent('viz-toggle', { detail: { type: 'trail', value: checked } }));
    });
  }

  // Helper methods

  private createSelect(id: string, options: Array<{ value: string; label: string }>, selected: string): HTMLSelectElement {
    const select = document.createElement('select');
    select.id = id;

    for (const option of options) {
      const optionEl = document.createElement('option');
      optionEl.value = option.value;
      optionEl.textContent = option.label;
      if (option.value === selected) {
        optionEl.selected = true;
      }
      select.appendChild(optionEl);
    }

    return select;
  }

  private addNumberInputRow(
    parent: HTMLElement,
    inputs: Array<{
      label: string;
      value: number;
      min: number;
      max: number;
      step: number;
      onChange: (value: number) => void;
    }>
  ): void {
    const row = document.createElement('div');
    row.className = 'input-row';

    for (const inputSpec of inputs) {
      const container = document.createElement('div');
      container.className = 'input-col';

      const labelEl = document.createElement('label');
      labelEl.textContent = inputSpec.label;
      labelEl.className = 'input-label';

      const input = document.createElement('input');
      input.type = 'number';
      input.className = 'input-compact';
      input.value = inputSpec.value.toString();
      input.min = inputSpec.min.toString();
      input.max = inputSpec.max.toString();
      input.step = inputSpec.step.toString();
      input.addEventListener('input', () => {
        // Remove invalid class when user starts typing
        input.classList.remove('invalid');

        // Check if value is valid
        const value = parseFloat(input.value);
        if (input.value === '' || isNaN(value)) {
          input.classList.add('invalid');
        } else {
          inputSpec.onChange(value);
        }
      });

      container.appendChild(labelEl);
      container.appendChild(input);
      row.appendChild(container);
    }

    parent.appendChild(row);
  }

  private addCheckbox(
    parent: HTMLElement,
    label: string,
    checked: boolean,
    onChange: (checked: boolean) => void
  ): void {
    const container = document.createElement('div');
    container.style.display = 'flex';
    container.style.alignItems = 'center';
    container.style.marginTop = '0.4rem';

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = checked;
    checkbox.id = `checkbox-${label.replace(/\s+/g, '-').toLowerCase()}`;
    checkbox.addEventListener('change', () => {
      onChange(checkbox.checked);
    });

    const labelEl = document.createElement('label');
    labelEl.htmlFor = checkbox.id;
    labelEl.textContent = label;
    labelEl.style.marginLeft = '0.5rem';
    labelEl.style.cursor = 'pointer';
    labelEl.style.fontSize = '0.85rem';

    container.appendChild(checkbox);
    container.appendChild(labelEl);
    parent.appendChild(container);
  }

  /**
   * Validate all number inputs in the control panel
   */
  private validateInputs(): boolean {
    const allInputs = this.container.querySelectorAll('input[type="number"]');
    let hasInvalid = false;

    allInputs.forEach((input) => {
      const htmlInput = input as HTMLInputElement;
      const value = htmlInput.value;

      if (value === '' || isNaN(parseFloat(value))) {
        htmlInput.classList.add('invalid');
        hasInvalid = true;
      } else {
        htmlInput.classList.remove('invalid');
      }
    });

    return !hasInvalid;
  }

  /**
   * Check if inputs are valid before starting simulation
   */
  canStartSimulation(): boolean {
    const isValid = this.validateInputs();

    if (!isValid) {
      (window as any).showNotification?.('Please fill in all input fields');
    }

    return isValid;
  }

  /**
   * Apply configuration changes immediately, pause, and reset simulation
   */
  private applyConfigAndPause(): void {
    const error = validateConfig(this.currentConfig);
    if (error) {
      console.warn('Configuration error:', error);
      return;
    }

    // Pause simulation and update button state
    this.callbacks.onPause();
    const startPauseBtn = document.getElementById('start-pause-btn');
    if (startPauseBtn) {
      startPauseBtn.textContent = '▶';
    }

    // Apply new configuration (this will reset the simulation)
    this.callbacks.onConfigChange(this.currentConfig);

    // Explicitly reset to ensure clean state at t=0
    this.callbacks.onReset();
    (window as any).showNotification?.('Configuration updated');
  }

  /**
   * Get current configuration
   */
  getConfig(): SimConfig {
    return { ...this.currentConfig };
  }
}
